import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import { InputTextArea } from './input.TextArea';
import { InputSimple } from './input.Simple';
import { TextMd } from '../text/text.Md';
import { BtnSimple } from './btn.Simple';
import { ModalConfirm } from '../modal/modal.confirm';
import { useDispatch } from 'react-redux';
import { setOpenModales } from '../../store/global';

export const SETTINGS_FIELD_CLASS =
  'w-full min-h-38 rounded bg-main bg-opacity-50 text-contrast text-opacity-80 px-1 py-05 text-sm';
export const SETTINGS_FIELD_CLASS_SM =
  'w-full min-h-28 rounded bg-main bg-opacity-50 text-contrast text-opacity-80 px-05 py-01 text-xs';

export const WidgetSettingsRoot = props => {
  const { children, className = '' } = props;
  return (
    <div className={`flex flex-col gap-1 text-contrast ${className}`}>
      {children}
    </div>
  );
};

export const WidgetSettingsSection = props => {
  const { title, help, children, className = '' } = props;
  return (
    <div
      className={`rounded border border-primary border-opacity-20 bg-main bg-opacity-30 p-1 ${className}`}
    >
      {title && (
        <div className='text-xs uppercase text-contrast text-opacity-60 mb-05'>
          {title}
        </div>
      )}
      {help && (
        <div className='text-2xs text-contrast text-opacity-60 mb-05'>
          {help}
        </div>
      )}
      {children}
    </div>
  );
};

export const WidgetSettingsCollapsibleSection = props => {
  const { title, help, children, className = '', defaultOpen = false } = props;
  const [open, setOpen] = React.useState(Boolean(defaultOpen));

  return (
    <Accordion
      expanded={open}
      onChange={() => setOpen(prev => !prev)}
      disableGutters
      className={`bg-main bg-opacity-30 rounded border border-primary border-opacity-20 shadow-none text-contrast ${className}`}
    >
      <AccordionSummary className='text-contrast'>
        <div className='text-contrast flex items-center gap-05 flex-1 font-condensed text-lg'>
          {open ? <FaChevronUp /> : <FaChevronDown />}
          <span>{title}</span>
        </div>
      </AccordionSummary>
      <AccordionDetails className='flex flex-col gap-1 text-contrast'>
        {help && (
          <div className='text-2xs text-contrast text-opacity-60 mb-05'>
            {help}
          </div>
        )}
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export const SettingsRow = props => {
  const {
    label,
    children,
    className = '',
    labelClassName = '',
    dense = false,
  } = props;
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-05 ${
        dense ? '' : 'py-02'
      } ${className}`}
    >
      {label && (
        <div
          className={`text-sm text-contrast text-opacity-70 ${labelClassName}`}
        >
          {label}
        </div>
      )}
      <div className='flex-1 flex'>{children}</div>
    </div>
  );
};

export const SettingsInput = props => {
  const { className = '', onBlur, onChange, ...rest } = props;
  return (
    <div className='relative flex-1'>
      <InputSimple
        className={`${SETTINGS_FIELD_CLASS} !m-0 ${className}`}
        onBlur={onBlur}
        onChange={onChange}
        {...rest}
      />
    </div>
  );
};

export const SettingsStaticValue = props => {
  const { className = '', children } = props;
  return (
    <div className={`${SETTINGS_FIELD_CLASS} flex items-center ${className}`}>
      {children}
    </div>
  );
};

export const SettingsIconButton = props => {
  const { className = '', children, ...rest } = props;
  return (
    <button
      type='button'
      className={`min-h-38 shrink-0 rounded bg-main bg-opacity-40 px-075 py-075 flex items-center justify-center transition hover:opacity-50 ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export const SettingsSelect = props => {
  return <SettingsSelectCustom {...props} />;
};

export const SettingsTextArea = props => {
  const { className = '', ...rest } = props;
  return (
    <InputTextArea
      className={`${SETTINGS_FIELD_CLASS} w-full m-0 ${className}`}
      {...rest}
    />
  );
};

const coerceToEvent = (value, meta = {}) => ({
  target: {
    value,
    name: meta.name,
    id: meta.id,
  },
});

const normalizeSelectOptions = children => {
  // Supports <option value="...">Label</option>
  const opts = [];
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type !== 'option') {
      return;
    }
    const value = child.props?.value;
    const label =
      typeof child.props?.children === 'string'
        ? child.props.children
        : Array.isArray(child.props?.children)
          ? child.props.children.join('')
          : String(child.props?.children ?? '');
    opts.push({
      value,
      label,
      disabled: Boolean(child.props?.disabled),
    });
  });
  return opts;
};

export const SettingsSelectCustom = props => {
  const {
    className = '',
    buttonClassName = '',
    menuClassName = '',
    options,
    children,
    value,
    defaultValue,
    onChange,
    disabled,
    placeholder,
    menuMaxHeight = 220,
    size = 'md',
    ...rest
  } = props;

  const opts = React.useMemo(() => {
    if (Array.isArray(options)) {
      return options;
    }
    return normalizeSelectOptions(children);
  }, [options, children]);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(
    isControlled ? value : (defaultValue ?? opts?.[0]?.value),
  );
  const selectedValue = isControlled ? value : internalValue;

  React.useEffect(() => {
    if (isControlled) {
      setInternalValue(value);
    }
  }, [isControlled, value]);

  const selectedOpt = opts.find(o => o.value === selectedValue);
  const isSmall = size === 'sm' || size === 'small';

  const rootRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [menuDirection, setMenuDirection] = React.useState('down');

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onDocMouseDown = e => {
      if (!rootRef.current) {
        return;
      }
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () =>
      document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open]);

  const openMenu = () => {
    if (disabled) {
      return;
    }
    if (rootRef.current && typeof window !== 'undefined') {
      const rect = rootRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 0;
      const safeMaxHeight = Number(menuMaxHeight) || 220;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp =
        spaceBelow < Math.min(180, safeMaxHeight) && spaceAbove > spaceBelow;
      setMenuDirection(shouldOpenUp ? 'up' : 'down');
    }
    setOpen(true);
    const idx = opts.findIndex(o => o.value === selectedValue && !o.disabled);
    setHighlightedIndex(idx);
  };

  const closeMenu = () => {
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const commitValue = nextValue => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    onChange?.(coerceToEvent(nextValue, { name: rest.name, id: rest.id }));
  };

  const onKeyDown = e => {
    if (disabled) {
      return;
    }
    if (
      !open &&
      (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')
    ) {
      e.preventDefault();
      openMenu();
      return;
    }
    if (!open) {
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      let next = highlightedIndex;
      for (let i = 0; i < opts.length; i++) {
        next = (next + dir + opts.length) % opts.length;
        if (!opts[next]?.disabled) {
          setHighlightedIndex(next);
          break;
        }
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = opts[highlightedIndex];
      if (opt && !opt.disabled) {
        commitValue(opt.value);
        closeMenu();
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className={`relative flex-1 ${className}`}
      {...rest}
    >
      <button
        type='button'
        disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
        className={`${isSmall ? SETTINGS_FIELD_CLASS_SM : SETTINGS_FIELD_CLASS} ${isSmall ? 'text-sm' : ''} flex items-center justify-between gap-05 focus:outline-none ${
          disabled
            ? 'opacity-40 cursor-not-allowed'
            : 'cursor-pointer hover:bg-opacity-60'
        } ${buttonClassName}`}
      >
        <span className={`truncate ${isSmall ? 'text-sm' : ''}`}>
          {selectedOpt?.label ?? placeholder ?? '—'}
        </span>
        <span className={`shrink-0 transition ${open ? 'rotate-180' : ''}`}>
          <FaChevronDown />
        </span>
      </button>

      {open && (
        <div
          className={`absolute left-0 right-0 z-50 rounded border border-primary border-opacity-20 bg-main bg-opacity-90 backdrop-blur-sm shadow-lg overflow-auto ${
            menuDirection === 'up' ? 'bottom-full mb-02' : 'top-full mt-02'
          } ${menuClassName}`}
          style={{ maxHeight: menuMaxHeight }}
          role='listbox'
        >
          {opts.map((opt, idx) => {
            const isSelected = opt.value === selectedValue;
            const isHighlighted = idx === highlightedIndex;
            return (
              <button
                key={String(opt.value) + idx}
                type='button'
                disabled={opt.disabled}
                onMouseEnter={() => !opt.disabled && setHighlightedIndex(idx)}
                onClick={() => {
                  if (opt.disabled) {
                    return;
                  }
                  commitValue(opt.value);
                  closeMenu();
                }}
                className={`w-full text-left ${isSmall ? 'px-05 py-02 text-xs' : 'px-1 py-05 text-sm'} ${
                  opt.disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-pointer'
                } ${isHighlighted ? 'bg-primary bg-opacity-20' : ''} ${
                  isSelected ? 'text-contrast' : 'text-contrast text-opacity-70'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const HelpMarkdownSection = props => {
  const {
    title = "Texte d'aide",
    value,
    onChange,
    placeholder = 'Markdown supporté (titres, listes, etc.)',
    previewTextId,
    previewItemId,
    defaultOpen = false,
  } = props;

  return (
    <WidgetSettingsCollapsibleSection
      title={title}
      defaultOpen={defaultOpen}
    >
      <div className='flex flex-col gap-05 w-full'>
        <SettingsTextArea
          placeholder={placeholder}
          initValue={value ?? ''}
          rows={3}
          maxRows={10}
          onBlur={nextValue => {
            const trimmed =
              typeof nextValue === 'string' ? nextValue.trim() : '';
            const sanitized = trimmed === '' ? null : trimmed;
            onChange?.(sanitized);
          }}
        />
        {value && (
          <div className='rounded bg-main bg-opacity-40 p-05 text-sm text-contrast'>
            <TextMd
              str={value}
              textId={previewTextId || 'settings-help-preview'}
              itemId={previewItemId || 'settings-help'}
              style={{ width: '100%', color: 'inherit' }}
            />
          </div>
        )}
      </div>
    </WidgetSettingsCollapsibleSection>
  );
};

export const DangerZone = props => {
  const {
    title = 'Suppression',
    confirmTitle = 'Suppression',
    confirmText = 'Supprimer',
    message = 'Voulez-vous vraiment supprimer cet élément ?',
    confirmId,
    closeModalId,
    onConfirm,
  } = props;

  const dispatch = useDispatch();

  if (!confirmId) {
    return null;
  }

  return (
    <WidgetSettingsCollapsibleSection
      title={title}
      defaultOpen={false}
      className='border-red border-opacity-30'
    >
      <div className='flex items-center gap-05'>
        <div className='text-sm flex-1 text-contrast text-opacity-70'>
          {message}
        </div>
        <div className='flex justify-end'>
          <BtnSimple
            color='secondary'
            onClick={() =>
              dispatch(setOpenModales({ id: confirmId, open: true }))
            }
          >
            <div className='text-sm flex gap-1 items-center'>
              <FaTrash />
              Supprimer…
            </div>
          </BtnSimple>
        </div>
      </div>

      <ModalConfirm
        title={confirmTitle}
        idToOpen={confirmId}
        confirmText={confirmText}
        confirmCallback={() => {
          onConfirm?.();
          if (closeModalId) {
            dispatch(setOpenModales({ id: closeModalId, open: false }));
          }
        }}
      >
        {message}
      </ModalConfirm>
    </WidgetSettingsCollapsibleSection>
  );
};
